import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VerifiedIcon from "@mui/icons-material/Verified";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import { alpha } from "@mui/material/styles";
import { useTranslations } from "use-intl";
import useUser from "../../../../hooks/useUser";
import {
  getPosLanding,
  savePosDetails,
  updatePosDetails,
  getCustomerDetails,
} from "../../services";
import CopyButton from "../../../../shared/components/CopyButton/CopyButton";

const WONDERSOFT_LOGO =
  "https://media.licdn.com/dms/image/v2/C4E0BAQG0-4zc8JRMUw/company-logo_200_200/company-logo_200_200/0/1630583591742/wondersoft_retail_logo?e=2147483647&v=beta&t=ekyUzz3IVAG64KwfzsIinnN9GDumVaRTJJulw0GR6LM";
const GINESYS_LOGO =
  "https://gdm-catalog-fmapi-prod.imgix.net/ProductLogo/7c147df1-baf1-4779-a0cd-93d772b3349e.png?w=128&h=128&fit=max&dpr=3&auto=format&q=50";
const OTHERS_LOGO =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='128' height='128' viewBox='0 0 24 24' fill='none' stroke='%23757575' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='12' cy='12' r='2'></circle><circle cx='19' cy='12' r='2'></circle><circle cx='5' cy='12' r='2'></circle></svg>";

const POS_OPTIONS = [
  { value: "wondersoft", label: "Wondersoft", logo: WONDERSOFT_LOGO },
  { value: "genesys", label: "Ginesys", logo: GINESYS_LOGO },
  { value: "others", label: "Others", logo: OTHERS_LOGO },
];

interface CredentialsPanelProps {
  tenantId: number | null;
  businessId: number | null;
  tenantToken: string;
  apiKey: string;
  syncUser?: string;
  syncPassword?: string;
}

interface CredentialRowProps {
  label: string;
  value: string;
  isSensitive?: boolean;
}

const CredentialRow: React.FC<CredentialRowProps> = ({
  label,
  value,
  isSensitive = false,
}) => {
  const t = useTranslations("integrations");
  const [localVisible, setLocalVisible] = useState(false);
  const showValue = !isSensitive || localVisible;

  if (!value) return null;

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "flex-start", sm: "center" }}
      sx={{
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        color="text.secondary"
        sx={{
          width: { xs: "auto", sm: 180 },
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            px: 1.5,
            py: 0.5,
            bgcolor: "action.hover",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            width: { xs: "100%", sm: 320 },
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: showValue ? "text.primary" : "text.disabled",
          }}
        >
          {showValue ? value : "••••••••••••••••"}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          {isSensitive && (
            <Tooltip title={showValue ? t("pos.hideValue") : t("pos.showValue")}>
              <IconButton
                size="small"
                onClick={() => setLocalVisible(!localVisible)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 0.5,
                }}
              >
                {showValue ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          <CopyButton
            copyText={value}
            size="small"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 0.5,
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const CredentialsPanel: React.FC<CredentialsPanelProps> = ({
  tenantId,
  businessId,
  tenantToken,
  apiKey,
  syncUser,
  syncPassword,
}) => {
  const t = useTranslations("integrations");
  const [v1Copied, setV1Copied] = useState(false);
  const [v2Copied, setV2Copied] = useState(false);

  const hasV1Credentials =
    tenantId != null || businessId != null || tenantToken;

  const handleCopyV1 = () => {
    const lines = [
      tenantId != null ? `Tenant ID: ${tenantId}` : "",
      businessId != null ? `Business Unit ID: ${businessId}` : "",
      tenantToken ? `Tenant Token: ${tenantToken}` : "",
      syncUser ? `Sync Username: ${syncUser}` : "",
      syncPassword ? `Sync Password: ${syncPassword}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    navigator.clipboard.writeText(lines).then(() => {
      setV1Copied(true);
      setTimeout(() => setV1Copied(false), 1500);
    });
  };

  const handleCopyV2 = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(`api-key: ${apiKey}`).then(() => {
      setV2Copied(true);
      setTimeout(() => setV2Copied(false), 1500);
    });
  };

  return (
    <Stack spacing={2}>
      {!syncUser || !syncPassword ? (
        <Alert severity="info">{t("pos.fillDetailsInfo")}</Alert>
      ) : (
        <Alert severity="info">
          {t.rich("pos.apiUserGenerated", {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </Alert>
      )}

      {hasV1Credentials && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {t("pos.v1Credentials")}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color={v1Copied ? "success" : "primary"}
              startIcon={v1Copied ? <DoneIcon /> : <ContentCopyIcon />}
              onClick={handleCopyV1}
            >
              {v1Copied ? t("pos.copied") : t("pos.copyCredentials")}
            </Button>
          </Stack>

          <Box>
            <CredentialRow
              label={t("pos.tenantId")}
              value={tenantId != null ? String(tenantId) : ""}
            />
            <CredentialRow
              label={t("pos.businessUnitId")}
              value={businessId != null ? String(businessId) : ""}
            />
            <CredentialRow
              label={t("pos.tenantToken")}
              value={tenantToken}
              isSensitive
            />
            {syncUser && (
              <CredentialRow
                label={t("pos.syncUsername")}
                value={syncUser}
                isSensitive
              />
            )}
            {syncPassword && (
              <CredentialRow
                label={t("pos.syncPassword")}
                value={syncPassword}
                isSensitive
              />
            )}
          </Box>
        </Paper>
      )}

      {apiKey && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              api-key
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color={v2Copied ? 'success' : 'primary'}
              startIcon={v2Copied ? <DoneIcon /> : <ContentCopyIcon />}
              onClick={handleCopyV2}
            >
              {v2Copied ? t("pos.copied") : t("pos.copyKey")}
            </Button>
          </Stack>

          <Box>
            <CredentialRow label="api-key" value={apiKey} isSensitive />
          </Box>
        </Paper>
      )}
    </Stack>
  );
};

const PosIntegration: React.FC = () => {
  const t = useTranslations("integrations");
  const { currentUser, selectedBusiness } = useUser();
  const tenantId = currentUser?.tenantId ?? null;
  const businessId = selectedBusiness?.id ?? null;
  const businessName = selectedBusiness?.name ?? "";

  // Status from API
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [posDetail, setPosDetail] = useState<any>(null);
  const [tenantToken, setTenantToken] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  // Form state
  const [pos, setPos] = useState("");
  const [integrationNotes, setIntegrationNotes] = useState("");
  const [jsonData, setJsonData] = useState("{\n  \n}");
  const [otherPosName, setOtherPosName] = useState("");
  const [billSyncVerified, setBillSyncVerified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState("");
  const [saveError, setSaveError] = useState("");

  // Bill search state
  const [mobileNumber, setMobileNumber] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [billData, setBillData] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasSavedOnce = !!posDetail;

  const fetchStatus = async () => {
    if (!businessId) return;
    setStatusLoading(true);
    setStatusError("");
    try {
      const data = await getPosLanding(businessId);
      const details = (data.posDetails || []).find(
        (p: any) => p.tenantBrandBusinessId === businessId,
      );
      setPosDetail(details || null);
      setTenantToken(data.tenantToken || "");
      setApiKey(data.apiKey || "");
      setCompletedSteps(data.posCompletedSteps || []);
    } catch {
      setStatusError(t("pos.loadFailed"));
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, businessId]);

  useEffect(() => {
    if (posDetail) {
      setPos(posDetail.pos || "");
      setIntegrationNotes(posDetail.integrationNotes || "");
      setJsonData(
        posDetail.data ? JSON.stringify(posDetail.data, null, 2) : "{\n  \n}",
      );
      setOtherPosName(posDetail.data?.posName || "");
      const isVerified =
        completedSteps.includes(`POS_SETUP:VERIFY_BILL_SYNC:${businessId}`) ||
        posDetail.isBillSyncVerified ||
        false;
      setBillSyncVerified(isVerified);
    } else {
      setPos("");
      setIntegrationNotes("");
      setJsonData("{\n  \n}");
      setOtherPosName("");
      const isVerified =
        completedSteps.includes(`POS_SETUP:VERIFY_BILL_SYNC:${businessId}`) ||
        false;
      setBillSyncVerified(isVerified);
    }
    setSaveSuccess("");
    setSaveError("");
    setSearchError("");
    setBillData(null);
  }, [posDetail, businessId, completedSteps]);

  const handleSave = async () => {
    if (!tenantId || !businessId) return;

    if (!pos) {
      setSaveError(t("pos.selectProviderError"));
      return;
    }

    let parsedData: any = {};
    if (jsonData.trim()) {
      try {
        parsedData = JSON.parse(jsonData);
      } catch {
        setSaveError(t("pos.invalidJson"));
        return;
      }
    }

    if (pos === "others") {
      if (!otherPosName) {
        setSaveError(t("pos.enterPosNameError"));
        return;
      }
      parsedData.posName = otherPosName;
    }

    setSaving(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      const payload = {
        pos,
        integrationNotes,
        isBillSyncVerified: billSyncVerified,
        data: parsedData,
      };

      if (hasSavedOnce) {
        await updatePosDetails(businessId, payload);
      } else {
        await savePosDetails(businessId, payload);
      }
      setSaveSuccess(t("pos.saveSuccess"));
      await fetchStatus();
    } catch (err: any) {
      setSaveError(err?.message || t("pos.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleSearchBills = async () => {
    if (!mobileNumber || !businessId) {
      setSearchError(t("pos.enterMobileError"));
      return;
    }
    setSearching(true);
    setSearchError("");
    try {
      const data = await getCustomerDetails(
        mobileNumber,
        businessId,
        businessName,
      );
      setBillData(data);
      setDialogOpen(true);
    } catch (err: any) {
      setSearchError(err?.message || t("pos.fetchCustomerFailed"));
    } finally {
      setSearching(false);
    }
  };

  const bills = billData?.user?.bills || [];
  const latestBill = bills.length > 0 ? bills[0] : null;

  if (statusLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={4}>
      {statusError && <Alert severity="error">{statusError}</Alert>}

      {/* Credentials */}
      <CredentialsPanel
        tenantId={tenantId}
        businessId={businessId}
        tenantToken={tenantToken}
        apiKey={apiKey}
        syncUser={posDetail?.data?.email || posDetail?.data?.user || ""}
        syncPassword={posDetail?.data?.password || ""}
      />

      {saveSuccess && <Alert severity="success">{saveSuccess}</Alert>}
      {saveError && <Alert severity="error">{saveError}</Alert>}

      {/* Step 1 — POS Provider & Config */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Avatar
            sx={{
              width: 24,
              height: 24,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          >
            1
          </Avatar>
          <Typography variant="subtitle2" fontWeight={700}>
            {t("pos.selectProvider")}
          </Typography>
          {hasSavedOnce && (
            <Chip
              icon={<VerifiedIcon fontSize="small" />}
              label={t("pos.configured")}
              size="small"
              color="success"
              sx={{ borderRadius: 1 }}
            />
          )}
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
            mb: 3,
            maxWidth: 600,
          }}
        >
          {POS_OPTIONS.map((option) => {
            const isSelected = pos === option.value;
            const isOthers = option.value === "others";
            return (
              <Paper
                key={option.value}
                variant="outlined"
                onClick={() => !saving && setPos(option.value)}
                sx={{
                  p: 1.5,
                  cursor: saving ? "default" : "pointer",
                  borderColor: isSelected ? "primary.main" : "divider",
                  borderWidth: isSelected ? 2 : 1,
                  bgcolor: isSelected
                    ? (theme) => alpha(theme.palette.primary.main, 0.02)
                    : "background.paper",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 1.5,
                  transition: "all 0.2s ease",
                  position: "relative",
                  "&:hover": saving
                    ? {}
                    : {
                        borderColor: isSelected
                          ? "primary.main"
                          : "primary.light",
                        bgcolor: isSelected
                          ? (theme) => alpha(theme.palette.primary.main, 0.04)
                          : (theme) => alpha(theme.palette.primary.light, 0.1),
                        transform: "translateY(-1px)",
                      },
                }}
              >
                {isSelected && (
                  <CheckCircleIcon
                    color="primary"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      fontSize: 18,
                    }}
                  />
                )}

                {/* Logo wrapper */}
                <Box
                  sx={{
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isOthers ? (
                    <MoreHorizIcon color={isSelected ? "primary" : "action"} />
                  ) : (
                    <Box
                      component="img"
                      src={option.logo}
                      alt={option.label}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>

                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={isSelected ? "primary.main" : "text.primary"}
                >
                  {isOthers ? t("pos.others") : option.label}
                </Typography>
              </Paper>
            );
          })}
        </Box>

        <Stack spacing={2}>
          {pos === "others" && (
            <TextField
              fullWidth
              label={t("pos.posName")}
              placeholder={t("pos.posNamePlaceholder")}
              value={otherPosName}
              onChange={(e) => setOtherPosName(e.target.value)}
              size="small"
              disabled={saving}
            />
          )}

          <TextField
            fullWidth
            label={t("pos.integrationNotes")}
            placeholder={t("pos.integrationNotesPlaceholder")}
            multiline
            rows={3}
            value={integrationNotes}
            onChange={(e) => setIntegrationNotes(e.target.value)}
            size="small"
            disabled={saving}
          />

          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 1, color: "text.secondary" }}
            >
              {t("pos.jsonData")}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={jsonData}
              onChange={(e) => setJsonData(e.target.value)}
              size="small"
              disabled={saving}
              sx={{
                "& .MuiInputBase-input": { fontFamily: "monospace" },
              }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* Step 2 — Bill Sync Verification */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Avatar
            sx={{
              width: 24,
              height: 24,
              bgcolor: hasSavedOnce ? "primary.main" : "grey.300",
              color: hasSavedOnce ? "primary.contrastText" : "text.disabled",
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          >
            2
          </Avatar>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color={hasSavedOnce ? "text.primary" : "text.disabled"}
          >
            {t("pos.billSyncVerification")}
          </Typography>
          {!hasSavedOnce && (
            <Chip
              label={t("pos.saveStepOneFirst")}
              size="small"
              sx={{ borderRadius: 1 }}
            />
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("pos.billSyncDescription")}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={billSyncVerified}
                onChange={(e) => setBillSyncVerified(e.target.checked)}
                color="success"
                disabled={!hasSavedOnce}
              />
            }
            label={
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ mr: 1 }}
                color={
                  !hasSavedOnce
                    ? "text.disabled"
                    : billSyncVerified
                      ? "success.main"
                      : "text.secondary"
                }
              >
                {billSyncVerified ? t("pos.verified") : t("pos.notVerified")}
              </Typography>
            }
            labelPlacement="start"
            sx={{ mr: 0 }}
          />
        </Stack>

        {searchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {searchError}
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            label={t("pos.customerMobile")}
            placeholder={t("pos.customerMobilePlaceholder")}
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            disabled={searching || !hasSavedOnce}
          />
          <Button
            variant="outlined"
            onClick={handleSearchBills}
            disabled={searching || !mobileNumber || !hasSavedOnce}
            startIcon={searching ? <CircularProgress size={18} /> : undefined}
          >
            {searching ? t("pos.searching") : t("pos.searchBills")}
          </Button>
        </Stack>
      </Paper>

      {/* Save Button */}
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {saving
            ? t("pos.saving")
            : hasSavedOnce
              ? t("pos.updateDetails")
              : t("pos.saveDetails")}
        </Button>
      </Box>

      {/* Bill Results Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, px: 3, py: 2 }}>
          {t("pos.customerDetailsTitle")}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 2 }}>
          {latestBill ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                mt: 1,
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.success.main,
                    theme.palette.mode === "dark" ? 0.12 : 0.06,
                  ),
                borderColor: (theme) => alpha(theme.palette.success.main, 0.3),
              }}
            >
              <Typography
                variant="subtitle2"
                color="success.main"
                fontWeight={700}
                gutterBottom
              >
                {t("pos.latestBillFound")}
              </Typography>
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("pos.billNo")}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {latestBill.billNo}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("pos.date")}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {new Date(latestBill.date).toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ) : (
            <Alert severity="warning" sx={{ mb: 3, mt: 1 }}>
              {t("pos.noBills")}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
            {t("pos.rawResponse")}
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflowX: "auto",
              maxHeight: 400,
            }}
          >
            <pre style={{ margin: 0, fontFamily: "monospace" }}>
              {JSON.stringify(billData, null, 2)}
            </pre>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            color="inherit"
            variant="outlined"
          >
            {t("pos.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PosIntegration;
